// RESONATE_HASH_SCROLL_FIX_V1
// Handles two things that React Router's default behaviour doesn't:
//   1. Malformed hashes like `#agency-hero/?utm_source=...` - these break
//      native anchor scroll because the fragment ID contains extra chars.
//      We strip everything after the first '/' '?' '&' and scroll to the
//      cleaned id if it exists in the DOM.
//   2. Cross-persona hash aliases - e.g. `/#slack` maps to an agency
//      variant, so we redirect the user to `/agencies#slack` where the
//      agency Hero can pick it up.
//
// The redundant hero-section hashes (`/agencies#agency-hero`, `/pricing
// #pricing-hero`, `/done-for-you#dfy-hero` - internal analytics scroll-
// tracking anchors, not real navigation targets) are stripped from the
// address bar in src/index.js instead, BEFORE BrowserRouter mounts - doing
// it here (after mount, via history.replaceState) doesn't stick, because
// React Router v7 initializes its own history/location state from
// window.location on mount and doesn't pick up an out-of-band replaceState
// call made later, so it re-asserts its cached hash back onto the URL.
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AD_VARIANTS } from "@/lib/adVariants";

// Keep this in sync with HASH_ALIAS in adVariants.js.
// It's duplicated intentionally (no circular import risk) so we can decide
// persona routing without pulling the whole variants map here.
const HASH_ALIAS_PERSONA = {
  slack: "agency",
  "no-time": "founder",
  ghostwriter: "agency",
  mcp: "founder",
  ideas: "founder",
  robot: "founder",
  predict: "founder",
  building: "founder",
  buried: "agency",
  "7-minutes": "agency",
};

const PERSONA_PATH = { founder: "/", agency: "/agencies" };

export const HashScrollFix = () => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hash) return;
    const clean = hash.replace(/^#/, "").split(/[/?&]/)[0];
    if (!clean) return;

    // (2) Cross-persona hash alias - redirect to the right route so the
    //     matching Hero (founder vs agency) actually renders the variant.
    const alias = HASH_ALIAS_PERSONA[clean];
    if (alias) {
      const targetPath = PERSONA_PATH[alias];
      if (targetPath && pathname !== targetPath) {
        navigate(`${targetPath}#${clean}`, { replace: true });
        return;
      }
    }
    // Also: a direct variant slug hash like #ceo_ideas_buried_in_slack
    if (AD_VARIANTS[clean]) {
      const targetPath = PERSONA_PATH[AD_VARIANTS[clean].persona];
      if (targetPath && pathname !== targetPath) {
        navigate(`${targetPath}#${clean}`, { replace: true });
        return;
      }
    }

    // (1) Scroll to the cleaned DOM id if it exists.
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(clean);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempts++ < 40) {
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
  }, [pathname, hash, navigate]);

  return null;
};
